import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941XInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 941-X Instructions for 2026: Complete Guide"
          description="Learn more with our step-by-step guide to accurately complete Form 941-X for 2026. Correct errors on your tax return and ensure compliance with IRS regulations"
          keywords=""
          canonical="resources/employment-tax/form-941-x-instructions/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 941-X</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Need to Correct a Previously Filed Form 941? File Form 941-X Easily
          and Accurately with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941xinstructions`}
          >
            File 941-X Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-x-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },

                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941-X at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        Form 941-X,{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          Adjusted Employer's Quarterly Federal Tax Return or
                          Claim for Refund
                        </span>
                        , is used by employers to correct errors in a previously
                        filed{' '}
                        <Link
                          href="/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Form 941
                        </Link>{' '}
                        with the IRS.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        It is used to fix mistakes related to employee wages,
                        tips, federal income tax withheld,{' '}
                        <Link
                          href="/resources/employment-tax/fica-tax/"
                          sx={{ color: '#0202ff' }}
                        >
                          Social Security and Medicare
                        </Link>{' '}
                        taxes, qualified payroll tax credits, and other payroll
                        tax reporting errors.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        A{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          separate Form 941-X
                        </span>{' '}
                        must be <span style={{ fontWeight: 600 }}>
                          {' '}
                          filed
                        </span>{' '}
                        for{' '}
                        <span style={{ fontWeight: 600 }}>
                          each quarter being corrected
                        </span>{' '}
                        . The form should be filed as soon as an error is
                        discovered.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Form 941-X , the Adjusted Employer’s Quarterly Federal Tax
                  Return or Claim for Refund Form, is crucial for correcting
                  errors on previously filed{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 941
                  </Link>
                  . This guide provides detailed instructions on how to
                  accurately complete and file Form 941-X for the 2026 tax year,
                  ensuring compliance with the latest IRS updates and
                  regulations
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#purpose" sx={{ color: '#0202ff' }}>
                          Purpose of Form 941-X
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What’s New for the 2026 Tax Year?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Step-by-Step Instructions for Filing Form 941-X for
                          2026
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#specific" sx={{ color: '#0202ff' }}>
                          Specific Instructions for Correcting Errors on Form
                          941
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="purpose"
                >
                  Purpose of Form 941-X:
                </Typography>
                <Typography variant="body1">
                  Form 941-X is used to correct errors on a previously filed
                  Form 941. Corrections can include:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Wages, tips, and other compensation
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Federal income tax withheld
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Taxable Social Security wages and tips
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Taxable Medicare wages and tips
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Additional Medicare Tax withholding
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Qualified small business payroll tax credit
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Credits for qualified sick and family leave wages (specific
                    periods)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Employee retention credit
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    COBRA premium assistance credit
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Employers must file a separate Form 941-X for each Form 941{' '}
                  being corrected. Ensure you have filed all applicable Forms
                  <Link
                    href="/resources/information-returns/form-w-2-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    {' '}
                    W-2{' '}
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/resources/information-returns/form-w-2c-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    W-2c{' '}
                  </Link>{' '}
                  with the SSA before submitting{' '}
                  <Link
                    href="/employment-tax/efile-irs-form-941-x-online/"
                    sx={{ color: '#2020ff' }}
                  >
                    IRS Form 941-X
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What’s New for the 2026 Tax Year?
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Federal income tax withholding on qualified tips and qualified
                  overtime compensation:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    For tax years 2025 through 2028, the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      One Big Beautiful Bill Act
                    </span>{' '}
                    (P.L. 119-21) allows individuals to claim new deductions on
                    their federal income tax returns.
                  </li>

                  <li style={{ marginBottom: 12 }}>
                    Employees and self-employed workers can deduct up to{' '}
                    <span style={{ fontWeight: 600 }}>
                      $25,000 in qualified tips
                    </span>{' '}
                    from jobs that regularly received tips before December 31,
                    2024.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Individuals can also deduct up to{' '}
                    <span style={{ fontWeight: 600 }}>$12,500</span> in
                    qualified overtime pay (
                    <span style={{ fontWeight: 600 }}>$25,000</span> for married
                    couples filing jointly).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Employers must use the employee’s updated Form W-4 and the
                    withholding methods in{' '}
                    <span style={{ fontWeight: 600 }}>Publication 15-T</span> to
                    adjust federal income tax withholding so employees can
                    receive more money in their paychecks.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Federal income tax withholding errors can generally be
                    corrected only within the same calendar year the wages were
                    paid. Errors from previous years usually can’t be corrected
                    using Form 941-X.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Tips of{' '}
                    <span style={{ fontWeight: 600 }}> $20 or more</span> per
                    month and overtime pay are still subject to Social Security
                    and Medicare taxes.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Aggregate return filers:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    A new{' '}
                    <span style={{ fontWeight: 600 }}>
                      “Aggregate Return Filers Only”
                    </span>{' '}
                    section on the form requires filers to indicate whether they
                    are:
                  </li>
                  <ul>
                    <li>A section 3504 agent</li>
                    <li>
                      A Certified Professional Employer Organization (CPEO)
                    </li>
                    <li>Another third-party filer </li>
                  </ul>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Limitation on COVID-19 related employee retention credit
                  corrections and extension of assessment period:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Under P.L. 119-21, businesses can’t claim or correct the
                    Employee Retention Credit (ERC) for the 3rd and 4th quarters
                    of 2021 unless the claim was filed on or before January 31,
                    2024.{' '}
                  </li>
                  <li>
                    The law also extends the IRS assessment period to 6 years
                    from the date the credit was claimed or when the original
                    return was filed, whichever is later.
                  </li>
                  <li>
                    Because of this extended review period, employers should
                    keep records related to qualified wages for the COVID-19
                    related Employee Retention Credit (ERC) wages paid after
                    June 30, 2021, and before January 1, 2022, for at least 7
                    years.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Specific Changes:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>Deferred Social Security Tax:</b> Lines 24 and 33b, used
                    for correcting deferred Social Security tax, are reserved
                    but can still be corrected using the April 2023 revision of
                    Form 941-X within the period of limitations.
                  </li>

                  <li style={{ marginBottom: 12 }}>
                    <b> Employee Retention Credit:</b> Lines 33a and 34 for
                    correcting qualified wages for the employee retention credit
                    for March 13-31, 2020, are reserved.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Qualified Sick and Family Leave Wages:</b> Leave credits
                    taken after March 31, 2020, and before October 1, 2021, have
                    expired. If paid in 2024, claim the credit using Form 941-X
                    after filing Form 941.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Discontinued Forms:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Form 941-SS and 941-PR are discontinued after 2023.
                    Employers in relevant U.S. territories should{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-941/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      file Form 941
                    </Link>{' '}
                    or the new Form 941 (sp) for Spanish instructions.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Reminders:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Qualified small businesses can now elect up to $500,000 for
                    the payroll tax credit for increasing research activities
                    starting after December 31, 2022.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Expired Credits:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Employee retention credit for wages paid before January 1,
                    2023, has expired.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    COBRA premium assistance credit period ended on September
                    30, 2021. Claims must now be made using Form 941-X.
                  </li>
                </ul>
                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> Tax credits for qualified sick leave, family
                  leave, and COBRA premiums due to the pandemic are reflected on
                  Form 943.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="step"
                >
                  Step-by-Step Instructions for Filing Form 941-X for 2026
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-instruction-img.webp" />
                  <source srcSet="/assets/images/form-941-x-instruction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-instruction-img.png"
                    loading="lazy"
                    alt="Form 941-X: EIN, Name, complete address, return you’re correcting, and select the Quarter  "
                    width={755}
                    height={451}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>Separate Form for Each Quarter: </b>File a separate Form
                    941-X for each Form 941 being corrected.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>EIN, Name, and Address: </b>Enter your EIN, name, and
                    address in the provided spaces. Ensure your information is
                    on all pages and any attachments.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Return You’re Correcting: </b> Check the type of return
                    and the quarter you’re correcting at the top of page 1.
                    Enter the calendar year and quarter on pages 2-5.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Date of Error Discovery: </b> Enter the date you
                    discovered the error. If multiple errors were discovered at
                    different times, enter the earliest date and subsequent
                    dates on line 43.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Reporting Negative Amounts: </b>Use negative numbers for
                    reductions in tax (credits) and positive numbers for
                    additional tax (owed amounts).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Form Entries: </b> Type or print entries using Courier
                    font if possible. Complete and sign all five pages and
                    staple multiple sheets in the upper-left corner.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Penalties and Interest: </b> Corrections of underreported
                    tax amounts may not incur penalties or interest if filed on
                    time, paid on line 27, and accompanied by a detailed
                    explanation for the correction.
                  </li>
                </ol>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="specific"
                >
                  Specific Instructions for Correcting Errors on Form 941
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 941-X consists of five parts. Follow the instructions to
                  correct errors in previously filed Form 941:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part 1: Select ONLY One Process
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-select-only.webp" />
                  <source srcSet="/assets/images/form-941-x-select-only.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-select-only.png"
                    loading="lazy"
                    alt="Form 941-X Part 1- Lines 1 and 2: adjusted return or claim to correct underreported or overreported taxes"
                    width={703}
                    height={224}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  1. <b>Adjusted Employment Tax Return: </b> Check this box if
                  correcting underreported or overreported tax amounts and using
                  the adjustment process.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  2. <b>Claim: </b>Check this box if correcting only
                  overreported tax amounts and claiming a refund or abatement.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part 2: Complete the Certifications
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-completed-certification.webp" />
                  <source srcSet="/assets/images/form-941-x-completed-certification.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-completed-certification.png"
                    loading="lazy"
                    alt="2026 IRS Form 941-X Part 2 Line 3: certification for W-2 or W-2c"
                    width={766}
                    height={119}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  3. <b>Filing Forms W-2 or W-2c: </b> Certify that you have
                  filed or will file corrected Forms W-2 or W-2c with the SSA.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-certifying-overreporting.webp" />
                  <source srcSet="/assets/images/form-941-x-certifying-overreporting.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-certifying-overreporting.png"
                    loading="lazy"
                    alt="IRS Form 941-X Line 4: options for adjusting overreported employment taxes."
                    width={765}
                    height={192}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  4. <b>Certifying Overreporting Adjustments: </b> Certify
                  overreporting adjustments for federal income tax, Social
                  Security tax, Medicare tax, or Additional Medicare Tax.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-certifying-claim.webp" />
                  <source srcSet="/assets/images/form-941-x-certifying-claim.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-certifying-claim.png"
                    loading="lazy"
                    alt="IRS Form 941-X Line 5: claiming a refund or abatement of employment taxes"
                    width={767}
                    height={247}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  5. <b>Certifying Claims: </b> Certify claims for refund or
                  abatement of overreported amounts.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part 3: Enter the Corrections for This Quarter
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-corrections-quarter.webp" />
                  <source srcSet="/assets/images/form-941-x-corrections-quarter.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-corrections-quarter.png"
                    loading="lazy"
                    alt="IRS Form 941-X Part 3 Line 6,7, and 8: correction section for quarterly wage and tax adjustments "
                    width={766}
                    height={203}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  6. <b>Wages, Tips, and Other Compensation:</b> Correct errors
                  in wages, tips, and other compensation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  7. <b>Federal Income Tax Withheld</b> Correct federal income
                  tax withholding errors.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  8.<b>Taxable Social Security Wages:</b> Correct taxable Social
                  Security wages.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-qualified-wages-img.webp" />
                  <source srcSet="/assets/images/form-941-x-qualified-wages-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-qualified-wages-img.png"
                    loading="lazy"
                    alt="Form 941-X Line 9 to 13: Qualified sick and family leave wages, Taxable FICA tips "
                    width={766}
                    height={242}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  9. <b>Qualified Sick Leave Wages: </b> Correct qualified sick
                  leave wages.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  10. <b>Qualified Family Leave Wages:</b> Correct qualified
                  family leave wages.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  11. <b>Taxable Social Security Tips:</b> Correct taxable
                  Social Security tips.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  12. <b>Taxable Medicare Wages & Tips:</b> Correct taxable
                  Medicare wages and tips.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  13.{' '}
                  <b>
                    Taxable Wages & Tips Subject to Additional Medicare Tax
                    Withholding:
                  </b>{' '}
                  Correct Additional Medicare Tax withholding errors.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-section-demand-tax.webp" />
                  <source srcSet="/assets/images/form-941-x-section-demand-tax.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-section-demand-tax.png"
                    loading="lazy"
                    alt="Form 941-X Line 14 to 16: worksheet section showing tax adjustments, notice and demand, and payroll tax credit calculations "
                    width={766}
                    height={144}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  14.{' '}
                  <b>
                    Section 3121(q) Notice and Demand—Tax on Unreported Tips:
                  </b>{' '}
                  Correct any amounts reported on{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form 941
                  </Link>{' '}
                  line 5f for tax due from a Section 3121(q) Notice and Demand.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  15. <b>Tax Adjustments:</b> Correct adjustments reported on
                  Form 941 lines 7–9.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  16.{' '}
                  <b>
                    Qualified Small Business Payroll Tax Credit for Increasing
                    Research Activities:
                  </b>{' '}
                  Correct the payroll tax credit reported on Form 941 line 11a.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-nonrefundable-img.webp" />
                  <source srcSet="/assets/images/form-941-x-nonrefundable-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-nonrefundable-img.png"
                    loading="lazy"
                    alt="Form 941-X Line 17 to 18d: worksheet showing nonrefundable credits for sick leave, family leave, and COBRA premium assistance"
                    width={767}
                    height={249}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  17.{' '}
                  <b>
                    Nonrefundable Portion of Credit for Qualified Sick and
                    Family Leave Wages:
                  </b>{' '}
                  Correct the nonrefundable portion of the credit for qualified
                  sick and family leave wages for leave taken after March 31,
                  2020, and before April 1, 2021.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  18a. Reserved for future use
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  18b.{' '}
                  <b>
                    Nonrefundable Portion of Credit for Qualified Sick and
                    Family Leave Wages:
                  </b>{' '}
                  Correct the nonrefundable portion of the credit for qualified
                  sick and family leave wages for leave taken after March 31,
                  2021, and before October 1, 2021.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  18c.{' '}
                  <b>
                    Nonrefundable Portion of COBRA Premium Assistance Credit:
                  </b>{' '}
                  Correct the nonrefundable portion of the COBRA premium
                  assistance credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  18d.{' '}
                  <b>
                    Number of Individuals Provided COBRA Premium Assistance:
                  </b>{' '}
                  Correct the number of individuals provided COBRA premium
                  assistance.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-special-add-tax.webp" />
                  <source srcSet="/assets/images/form-941-x-special-add-tax.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-special-add-tax.png"
                    loading="lazy"
                    alt="Form 941-X Line 19 to 22: showing special wage adjustments and corrections for federal, Social Security, and Medicare taxes "
                    width={766}
                    height={384}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  19. <b>Special Additions to Wages for Federal Income Tax:</b>{' '}
                  Correct wages for workers reclassified under Section 3509
                  rules affecting federal income tax withholding.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  20.{' '}
                  <b>Special Additions to Wages for Social Security Taxes:</b>{' '}
                  Correct wages for workers reclassified under Section 3509
                  rules affecting Social Security taxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  21. <b>Special Additions to Wages for Medicare Taxes:</b>{' '}
                  Correct wages for workers reclassified under Section 3509
                  rules affecting Medicare taxes.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  22.{' '}
                  <b>Special Additions to Wages for Additional Medicare Tax:</b>{' '}
                  Correct wages for workers reclassified under Section 3509
                  rules affecting Additional Medicare taxes.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-subtotal.webp" />
                  <source srcSet="/assets/images/form-941-x-subtotal.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-subtotal.png"
                    loading="lazy"
                    alt="Form 941-X Line 23: combine the amounts on lines 7 to 22 of column 4"
                    width={766}
                    height={38}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  23. <b>Subtotal:</b> Combine the amounts from column 4 on
                  lines 7–22.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-941-x-refundable-portion.webp" />
                  <source srcSet="/assets/images/form-941-x-refundable-portion.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-refundable-portion.png"
                    loading="lazy"
                    alt="Form 941-X line 24 to 26c: refundable credit section for sick leave, family leave, and COBRA premium assistance."
                    width={766}
                    height={227}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  24. Reserved for future use
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  25.{' '}
                  <b>
                    {' '}
                    Refundable Portion of Credit for Qualified Sick and Family
                    Leave Wages for Leave Taken After March 31, 2020, and Before
                    April 1, 2021:
                  </b>{' '}
                  Correct the refundable portion of the credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  26a. Reserved for future use
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  26b.{' '}
                  <b>
                    {' '}
                    Refundable Portion of Credit for Qualified Sick and Family
                    Leave Wages for Leave Taken After March 31, 2021, and Before
                    October 1, 2021:
                  </b>{' '}
                  Correct the refundable portion of the credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  26c.{' '}
                  <b> Refundable Portion of COBRA Premium Assistance Credit:</b>{' '}
                  Correct the refundable portion of the COBRA premium assistance
                  credit.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-941-x-total.webp" />
                  <source srcSet="/assets/form-941-x-total.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-total.png"
                    loading="lazy"
                    alt="Form 941-X line 27:  total calculation section showing credit, refund, or amount owed "
                    width={767}
                    height={133}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  27. <b>Total:</b> Combine the amounts from column 4 on lines
                  23–26c.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-941-x-qualified-health-plan.webp" />
                  <source srcSet="/assets/images/form-941-x-qualified-health-plan.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-qualified-health-plan.png"
                    loading="lazy"
                    alt="Form 941-X Lines 28 and 29: section for qualified health plan expenses related to sick and family leave wages "
                    width={767}
                    height={132}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  28.{' '}
                  <b>
                    Qualified Health Plan Expenses Allocable to Qualified Sick
                    Leave Wages for Leave Taken After March 31, 2020, and Before
                    April 1, 2021:
                  </b>{' '}
                  Correct the qualified health plan expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  29.{' '}
                  <b>
                    Qualified Health Plan Expenses Allocable to Qualified Family
                    Leave Wages for Leave Taken After March 31, 2020, and Before
                    April 1, 2021
                  </b>{' '}
                  Correct the qualified health plan expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Line 30 to 34 is reserved for future use.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Caution: Lines 35–40 apply only to quarters beginning after
                  March 31, 2021.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-line-35-40-quarters.webp" />
                  <source srcSet="/assets/images/form-941-x-line-35-40-quarters.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-line-35-40-quarters.png"
                    loading="lazy"
                    alt="Form 941-X Lines 35 to 40: section showing qualified sick and family leave wages and related health plan expenses "
                    width={767}
                    height={592}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  35.{' '}
                  <b>
                    {' '}
                    Qualified Sick Leave Wages for Leave Taken After March 31,
                    2021, and Before October 1, 2021:
                  </b>{' '}
                  Correct the amounts under certain collectively bargained
                  agreements allocable to qualified sick leave wages.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  36.{' '}
                  <b>
                    {' '}
                    Qualified Health Plan Expenses Allocable to Qualified Sick
                    Leave Wages for Leave taken After March 31, 2021, and Before
                    October 1, 2021:
                  </b>{' '}
                  Correct the qualified health plan expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  37.{' '}
                  <b>
                    {' '}
                    Amounts Under Certain Collectively Bargained Agreements
                    Allocable to Qualified Sick Leave Wages for Leave Taken
                    After March 31, 2021, and Before October 1, 2021:
                  </b>{' '}
                  Correct the amounts under certain collectively bargained
                  agreements allocable to qualified family leave wages.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  38.{' '}
                  <b>
                    {' '}
                    Qualified Family Leave Wages for Leave Taken After March 31,
                    2021, and Before October 1, 2021:
                  </b>{' '}
                  Correct the qualified health plan expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  39.{' '}
                  <b>
                    {' '}
                    Qualified Health Plan Expenses Allocable to Qualified Family
                    Leave Wages for Leave Taken After March 31, 2021, and Before
                    October 1, 2021:
                  </b>{' '}
                  Correct the qualified health plan expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  40.{' '}
                  <b>
                    {' '}
                    Amounts Under Certain Collectively Bargained Agreements
                    Allocable to Qualified Family Leave Wages for Leave Taken
                    After March 31, 2021, and Before October 1, 2021:
                  </b>{' '}
                  Correct the amounts under certain collectively bargained
                  agreements allocable to qualified family leave wages.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part 4: Explain Your Corrections for This Quarter
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-coorection-quarter-img.webp" />
                  <source srcSet="/assets/images/form-941-x-coorection-quarter-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-coorection-quarter-img.png"
                    loading="lazy"
                    alt="Form 941-X Lines 41 to 43: Correction form section for payroll reporting with checkboxes and explanation field."
                    width={767}
                    height={563}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  41.{' '}
                  <b>
                    {' '}
                    Corrections of Both Underreported and Overreported Amounts:
                  </b>{' '}
                  Provide a net change amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  42. <b> Reclassification of Workers:</b> Explain the
                  reclassification of workers if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  43. <b> Detailed Explanation:</b> Provide detailed
                  explanations for each correction, specifying the Form 941-X
                  line numbers, the date the error was discovered, the amount of
                  the error, and the cause of the error.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part 5: Sign Here
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-941-x-sign-here.webp" />
                  <source srcSet="/assets/images/form-941-x-sign-here.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-941-x-sign-here.png"
                    loading="lazy"
                    alt="Form 941-X Part 5: Tax form signature section for taxpayer and paid preparer with fields for contact details "
                    width={766}
                    height={374}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Ensure all five pages of Form 941-X are completed and signed.
                  The appropriate individual must sign the form based on the
                  type of business entity. Failure to sign will delay
                  processing.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
              '& li': { fontSize: 16 },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Why should you e-file Form 941 with TaxZerone?
            </Typography>
            <List sx={{ mb: 3, textAlign: 'left', maxWidth: 750, mx: 'auto' }}>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  IRS-authorized e-file service provider
                </ListItemText>
              </ListItem>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  Smart IRS validations to reduce errors in the return
                </ListItemText>
              </ListItem>
              <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                  <DoubleArrowIcon />
                </ListItemIcon>
                <ListItemText>
                  Supports Schedule B & Form 8974 to report any additional tax
                  liabilities and tax credit amounts accurately
                </ListItemText>
              </ListItem>
            </List>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              Simplify your quarterly payroll tax filing with TaxZerone at just{' '}
              <b>$6.99 per return</b>.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions2023`}
              >
                E-file Form 941 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container> */}
    </AppLayout>
  );
};

export default Form941XInstructions;
